import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { FileuploadComponent } from '@shared/components/fileupload/fileupload.component';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { DepartmentService, IBaseDepartment, IDepartmentDetail } from '@shared/services/department.service';
import { MesDepartmentTransferService } from '@shared/services/export-import-services/mesDepartmentTransfer.service';
import { ImportValidatorService } from '@shared/services/filevalidators/Importvalidator.service';
import { EmployeeService, IMesUser } from '@shared/services/mesuser.service';
import { NotificationService } from '@shared/services/notification.service';
import { SessionStorageService } from '@shared/services/storage.service';
import { ITeamProfile, TeamProfileService } from '@shared/services/teamProfiles.service';
import { IUser, UserService } from '@shared/services/user.service';
import { UserPermissionsService } from '@shared/services/userpermissions.service';
import { finalize, forkJoin, map, Subject, takeUntil } from 'rxjs';
import { CreateUpdateEmployeeDialogComponent } from '../mes-employee/create-update-employee/create-update-employee';
import { CreateUpdateDepartmentDialogComponent } from './create-update-department/create-update-department';

interface ITableViewModelItem {
	isActive: boolean;
	alias: string;
	approver: string;
	backup: string;
	department: IDepartmentDetail;
	id: string;
	name: string;
}

@Component({
	selector: 'app-department',
	templateUrl: './department.component.html',
	styleUrls: ['./department.component.scss'],
	animations: [trigger('detailExpand', [state('collapsed, void', style({ height: '0px' })), state('expanded', style({ height: '*' })), transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')), transition('expanded <=> void', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))])],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class DepartmentComponent implements OnInit {
	private destroy$ = new Subject<void>();
	private unsubscribe = new Subject<void>();
	private isInitialLoad = true;
	private users: Map<string, IUser> = new Map();
	private employees: Map<string, IMesUser[]> = new Map();
	private teamProfiles: Map<string, ITeamProfile> = new Map();
	private expandedElementIds = new Set<string>();
	public expandedElements: ITableViewModelItem[] = [];
	public showSearch = false;
	public isLoading = false;
	public initialized = false;
	public showInactive = false;
	public dataSource = new MatTableDataSource<ITableViewModelItem>();

	@ViewChild(MatSort) sort: MatSort;

	displayedColumns = ['name', 'approver', 'backup', 'isActive', 'actions'];
	innerDisplayedColumns = ['alias', 'teamProfileId', 'extendedActions'];
	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_expand_all',
			text: () => 'masterdata.cutting.toggle-expand-btn',
			click: () => this.toggleAll(),
			disabled: () => !this.initialized,
		},
		{
			icon: () => 'acies_add',
			text: () => 'mes.administration.department.dialog.create-department-btn',
			click: () => this.openCreateEditUpdateDepartment(),
			disabled: () => !this.initialized,
			permissions: () => ['mes:DepartmentCreate'],
		},
	];

	menuItems: SubMenuItem[] = [
		{
			icon: () => 'acies_download',
			text: () => 'mes.administration.department.import.button',
			click: () => this.onImport(),
		},
		{
			icon: () => 'acies_upload',
			text: () => 'mes.administration.department.export.button',
			click: () => this.onExport(),
		},
	];

	constructor(
		public dialog: MatDialog,
		public cd: ChangeDetectorRef,
		private userService: UserService,
		private departmentService: DepartmentService,
		private employeeService: EmployeeService,
		private teamProfileService: TeamProfileService,
		private sessionStorageService: SessionStorageService,
		public userPermissionsService: UserPermissionsService,
		private departmentTransferService: MesDepartmentTransferService,
		private injector: Injector,
		private notificationService: NotificationService,
	) {}

	ngOnInit(): void {
		this.dataSource.sort = this.sort;
		forkJoin({
			users: this.userService.fetchUsers(),
			employees: this.employeeService.getEmployees(),
			teamProfiles: this.teamProfileService.getTeamProfiles(),
		}).subscribe(({ users, employees, teamProfiles }) => {
			this.users = new Map(users.map((user) => [user.id, user]));
			this.teamProfiles = new Map(teamProfiles.result.map((teamProfile) => [teamProfile.id, teamProfile]));

			// Group employees by their departmentId
			employees.result.forEach((employee: IMesUser) => {
				if (employee.isActive) {
					if (this.employees.has(employee.departmentId)) {
						this.employees.get(employee.departmentId)?.push(employee);
					} else {
						this.employees.set(employee.departmentId, [employee]);
					}
				}
			});
			this.updateList();
			this.initialized = true;
		});
		this.listenForLanguageChangeAndLoadData();
	}

	private getITableViewModelItem(department: IDepartmentDetail): ITableViewModelItem {
		return {
			isActive: department.isActive,
			alias: department.alias,
			approver: department.shiftApprover?.name ?? '',
			backup: department.backupShiftApprover?.name ?? '',
			department,
			id: department.id,
			name: department.name,
		};
	}

	updateList() {
		if (this.isLoading) return;

		this.isLoading = true;

		this.expandedElementIds = new Set(this.expandedElements.map((e) => e.id));

		this.departmentService
			.getDepartments()
			.pipe(
				map((data) => this.mapDepartments(data.result)),
				map((departments) => this.filterDepartments(departments)),
				finalize(() => (this.isLoading = false)),
			)
			.subscribe((departments) => {
				this.dataSource.data = departments.map((department) => this.getITableViewModelItem(department));
				this.dataSource.sort = this.sort;
				this.onSort(this.sort);
				this.expandedElements = this.dataSource.data.filter((ds) => this.expandedElementIds.has(ds.id));
			});
	}

	// Helper function to map department data
	private mapDepartments(departments: IBaseDepartment[]): IDepartmentDetail[] {
		return departments.map(({ id, alias, name, shiftApprovers, isActive }) => ({
			id,
			alias,
			name,
			shiftApprover: this.getUser(shiftApprovers[0]),
			backupShiftApprover: this.getUser(shiftApprovers[1]),
			isActive,
			shiftApprovers,
			employees: new MatTableDataSource<IMesUser>(this.employees.get(id) || []),
		}));
	}

	private filterDepartments(departments: IDepartmentDetail[]): IDepartmentDetail[] {
		return this.showInactive ? departments : departments.filter((department) => department.isActive);
	}

	getUser(userId: string): IUser | undefined {
		return this.users?.get(userId);
	}

	getTeamProfile(id: string): ITeamProfile | undefined {
		return this.teamProfiles?.get(id);
	}

	applyFilter(event: KeyboardEvent) {
		if (event.target instanceof HTMLInputElement) {
			const filterValue = event.target.value;
			this.dataSource.filter = filterValue.trim().toLowerCase();
		}
	}

	onInnerSort(event: Sort, department: any) {
		const data = department.employees.data.slice();
		const direction = this.getDirectionFromSort(event);

		// Default sorting by alias if no active sort or direction is specified
		if (!event.active || event.direction === '') {
			department.employees.data = data.sort((a, b) => this.compare(a.alias, b.alias, 'asc'));
			return;
		}

		// Sort based on the active sort column using switch
		department.employees.data = data.sort((a, b) => {
			switch (event.active) {
				case 'alias': {
					return this.compare(a.firstName, b.firstName, direction);
				}
				case 'teamProfileId': {
					const firstEmployee = this.teamProfiles.get(a.teamProfileId).description;
					const secondEmployee = this.teamProfiles.get(b.teamProfileId).description;
					return this.compare(firstEmployee, secondEmployee, direction);
				}
				default:
					return 0;
			}
		});
	}

	listenForLanguageChangeAndLoadData() {
		this.sessionStorageService.watch('User.PreferredLanguage').subscribe(() => {
			if (this.isInitialLoad) {
				this.isInitialLoad = false;
			} else {
				if (this.dataSource) {
					this.updateList();
				}
			}
		});
	}

	toggleShowInactive() {
		this.showInactive = !this.showInactive;
		this.updateList();
	}

	toggleAll() {
		if (this.expandedElements.length < this.dataSource.data.length) {
			this.expandedElements = this.dataSource.data.slice();
		} else {
			this.expandedElements = [];
		}
	}

	toggleRow(element: ITableViewModelItem) {
		const index = this.expandedElements.indexOf(element);
		if (index >= 0) {
			this.expandedElements.splice(index, 1);
		} else {
			this.expandedElements.push(element);
		}
		this.cd.detectChanges();
	}

	openCreateEditUpdateDepartment(department: object = null) {
		const dialogRef = this.dialog.open(CreateUpdateDepartmentDialogComponent, {
			disableClose: true,
			width: '650px',
			data: {
				editDepartment: department,
				availableUsers: Array.from(this.users.values()),
			},
		});
		dialogRef.componentInstance.saved.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.updateList();
		});
	}

	onEditEmployeeClick(employee: IMesUser) {
		const dialogRef = this.dialog.open(CreateUpdateEmployeeDialogComponent, {
			disableClose: true,
			width: '450px',
			data: employee,
		});
		dialogRef.componentInstance.saved.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.updateList();
		});
	}

	private compare(a: string | boolean, b: string | boolean, direction: 'asc' | 'desc' = 'asc'): -1 | 0 | 1 {
		if (a < b) {
			return direction === 'asc' ? -1 : 1;
		} else if (a > b) {
			return direction === 'asc' ? 1 : -1;
		}
		return 0;
	}

	public onSort(sort: Sort) {
		if (sort.direction === '') {
			this.sort.sort({ id: 'alias', start: 'asc', disableClear: true });
		}
	}

	private getDirectionFromSort(sort: Sort): 'asc' | 'desc' {
		return sort.direction === 'desc' ? 'desc' : 'asc';
	}

	public hasPermissions(permissions: string[]): boolean {
		return this.userPermissionsService.hasPermissions(permissions);
	}

	ngOnDestroy() {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	onExport() {
		const department = this.dataSource.data.map((d) => d.department);

		this.departmentTransferService.exportData(department).subscribe({
			next: () => {
				this.notificationService.notify(['mes.administration.department.export.notification.success.header'], ['mes.administration.department.export.notification.success.content'], 'success');
			},
			error: (error) => {
				console.error('Failed to export data', error);
				this.notificationService.notify(['mes.administration.department.export.notification.error.header'], ['mes.administration.department.export.notification.error.content'], 'error');
			},
		});
	}

	onImport() {
		const dialogRef = this.dialog.open(FileuploadComponent, {
			width: '450px',
			data: {
				headingKey: 'mes.administration.department.import.title',
				subHeadingKey: 'mes.administration.department.import.subtitle',
				confirmButtonKey: 'mes.administration.department.import.button',
				notificationHeader: 'mes.administration.department.import.notification.success.header',
				notificationContent: 'mes.administration.department.import.notification.success.content',
				notificationErrorHeader: 'mes.administration.department.import.notification.error.header',
				notificationErrorContent: 'mes.administration.department.import.notification.error.content',
				notificationClientErrorHeader: 'mes.administration.department.import.notification.clientError.header',
				notificationClientErrorContent: 'mes.administration.department.import.notification.clientError.content',
				detailedClientErrorMessage: 'fileuploader.detailed-client-error-message',
				allowedFileTypes: ['.xlsx'],
				validatorService: this.injector.get(ImportValidatorService),
				data: this.departmentTransferService,
				optionalFields: [
					{
						name: 'overwrite',
						titleKey: 'mes.administration.department.import.overwrite',
						type: 'checkbox',
						value: false,
					},
				],
			},
		});

		dialogRef.afterClosed().subscribe(() => {
			this.updateList();
		});
	}
}
